import { NgOptimizedImage, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, signal } from '@angular/core';
import { GoogleAuthProvider, MultiFactorError } from '@angular/fire/auth';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@app/shared/data';
import { OtpDialogComponent } from '@app/shared/ui';
import { environment, Provider } from '@env';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { firstValueFrom } from 'rxjs';
type LoginStage = 'email' | 'password' | 'success' | 'forgotPassword' | 'loading';

@Component({
  selector: 'app-login',
  templateUrl: './login.html',

  imports: [
    NgOptimizedImage,
    TitleCasePipe,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CheckboxModule,
    DividerModule,
    ProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly _formBuilder = inject(FormBuilder);

  private readonly _authService = inject(AuthService);

  private readonly _messageService = inject(MessageService);

  private readonly _dialogService = inject(DialogService);

  private readonly _router = inject(Router);

  readonly businessName: string = environment.businessInfo.name;

  readonly logoPath: string = environment.businessInfo.logoPath;

  readonly providers: Provider[] = environment.businessInfo.providers;

  private readonly _document = inject(DOCUMENT);

  emailForm: FormGroup;

  passwordForm: FormGroup;

  readonly loginStep = signal<LoginStage>('email');

  constructor() {
    this.emailForm = this.initialiseEmailForm();
    this.passwordForm = this.initialisePasswordForm();
  }

  /**
   * Send a password reset link to an individual
   */
  async sendPasswordResetLink(): Promise<void> {
    this.loginStep.set('loading');
    try {
      await this._authService.sendPasswordResetEmail(this.emailForm.value['email'], this._document.location.href);
      this.loginStep.set('success');
    } catch {
      this._messageService.add({
        severity: 'error',
        summary: 'Something Went Wrong',
        detail: 'Please try and reset your password again.',
      });
      this.loginStep.set('email');
    }
  }

  /**
   * Handle user login and catch any errors
   */
  async handleUserLogin(): Promise<void> {
    this.loginStep.set('loading');
    try {
      await this._authService.setPersistence(this.passwordForm.value['rememberMe']);
      await this._authService.login(this.emailForm.value['email'], this.passwordForm.value['password']);

      // Navigate to login redirect component to wait for authentication
      this._router.navigateByUrl('/login-redirect');
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      switch (code) {
        case 'auth/wrong-password':
          this.loginStep.set('password');
          this._messageService.add({
            summary: 'Incorrect Password',
            detail: 'Please try again.',
            severity: 'error',
          });
          this.passwordForm = this.initialisePasswordForm();
          break;
        case 'auth/multi-factor-auth-required': {
          const result = await this.handleMultiFactorAuth(error as MultiFactorError);
          if (result) {
            this._router.navigateByUrl('/login-redirect');
          } else {
            this.loginStep.set('password');
          }
          break;
        }
        default:
          this.loginStep.set('password');
          this._messageService.add({
            summary: 'Login Failed',
            detail: 'Please try again.',
            severity: 'error',
          });
          this.passwordForm = this.initialisePasswordForm();
          break;
      }
    }
  }

  /**
   * Handle provider-based authentication (e.g., Google)
   */
  async handleProviderChoice(provider: Provider): Promise<void> {
    this.loginStep.set('loading');
    try {
      switch (provider) {
        case 'google':
          await this._authService.providerSignIn(GoogleAuthProvider.PROVIDER_ID);
          this._router.navigateByUrl('/login-redirect');
          break;
      }
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      switch (code) {
        case 'auth/multi-factor-auth-required': {
          const result = await this.handleMultiFactorAuth(error as MultiFactorError);
          if (result) {
            this._router.navigateByUrl('/login-redirect');
          }
          break;
        }
        default:
          this.loginStep.set('email');
          this._messageService.add({
            summary: 'Login Failed',
            detail: 'Please try again.',
            severity: 'error',
          });
          break;
      }
    }
  }

  /**
   * Shows OTP dialog and handles the multi-factor auth flow
   */
  private async handleMultiFactorAuth(error: MultiFactorError): Promise<boolean> {
    const phoneNumber: string = this._authService.getEnrolledPhoneNumber(error);
    const id: string = await this._authService.getSmsVerificationId(phoneNumber, error);
    return await firstValueFrom(
      this._dialogService.open(OtpDialogComponent, {
        header: 'Enter One Time Passcode',
        width: '30vw',
        breakpoints: { '1199px': '30vw', '575px': '90vw' },
        data: { id: id, error: error },
        closable: false,
        modal: true,
      }).onClose,
    );
  }

  /**
   * Initialise the email form
   */
  private initialiseEmailForm(): FormGroup {
    return this._formBuilder.group({
      email: ['', [Validators.email, Validators.required]],
    });
  }

  /**
   * Initialise the password form
   */
  private initialisePasswordForm(): FormGroup {
    return this._formBuilder.group({
      password: ['', [Validators.required]],
      rememberMe: [true],
    });
  }
}
